import { useState, useRef, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Search, X, Filter, Clock, Star, Paperclip, Calendar } from 'lucide-react'
import { useMailStore } from '../../stores/mailStore'
import { clsx } from 'clsx'

interface FilterChip {
  key: string
  label: string
  value: string
}

const QUICK_FILTERS = [
  { key: 'has_attachment', label: 'Has attachment', icon: Paperclip, value: 'true' },
  { key: 'is_unread', label: 'Unread', icon: Clock, value: 'true' },
  { key: 'is_starred', label: 'Starred', icon: Star, value: 'true' },
]

export function SearchBar() {
  const { searchQuery, setSearchQuery } = useMailStore()
  const [isFocused, setIsFocused] = useState(false)
  const [activeFilters, setActiveFilters] = useState<FilterChip[]>([])
  const [showFilterPanel, setShowFilterPanel] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)

  const hasContent = searchQuery.length > 0 || activeFilters.length > 0

  const addFilter = (key: string, label: string, value: string) => {
    if (!activeFilters.find((f) => f.key === key)) {
      setActiveFilters((prev) => [...prev, { key, label, value }])
    }
  }

  const removeFilter = (key: string) => {
    setActiveFilters((prev) => prev.filter((f) => f.key !== key))
  }

  const clearAll = () => {
    setSearchQuery('')
    setActiveFilters([])
    inputRef.current?.blur()
  }

  // Keyboard shortcut: / to focus
  useEffect(() => {
    const el = inputRef.current
    if (el) {
      el.setAttribute('data-search-input', '')
    }
  }, [])

  return (
    <div className={clsx('search-bar', { 'search-bar--focused': isFocused || hasContent })}>
      <div className="search-bar__inner">
        <Search size={15} className="search-bar__icon" />

        {/* Filter chips */}
        {activeFilters.map((filter) => (
          <span key={filter.key} className="search-filter-chip">
            {filter.label}
            <button
              className="search-filter-chip__remove"
              onClick={() => removeFilter(filter.key)}
            >
              <X size={10} />
            </button>
          </span>
        ))}

        <input
          ref={inputRef}
          id="search-mail"
          name="search"
          type="text"
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
          onFocus={() => setIsFocused(true)}
          onBlur={() => setTimeout(() => setIsFocused(false), 150)}
          placeholder="Search mail…"
          className="search-bar__input"
          aria-label="Search mail"
        />

        <div className="search-bar__actions">
          {hasContent && (
            <motion.button
              className="btn-icon search-bar__clear"
              onClick={clearAll}
              initial={{ opacity: 0, scale: 0.8 }}
              animate={{ opacity: 1, scale: 1 }}
              exit={{ opacity: 0, scale: 0.8 }}
            >
              <X size={13} />
            </motion.button>
          )}
          <button
            className={clsx('btn-icon search-bar__filter-btn', { active: showFilterPanel })}
            onClick={() => setShowFilterPanel((v) => !v)}
            title="Advanced filters"
          >
            <Filter size={13} />
          </button>
        </div>
      </div>

      {/* Quick filters + suggestions */}
      <AnimatePresence>
        {(isFocused || showFilterPanel) && (
          <motion.div
            className="search-dropdown"
            initial={{ opacity: 0, y: -6, scaleY: 0.95 }}
            animate={{ opacity: 1, y: 0, scaleY: 1 }}
            exit={{ opacity: 0, y: -6, scaleY: 0.95 }}
            style={{ transformOrigin: 'top' }}
          >
            {searchQuery === '' && (
              <>
                <div className="search-dropdown__section-label">Quick filters</div>
                <div className="search-dropdown__quick-filters">
                  {QUICK_FILTERS.map((qf) => {
                    const Icon = qf.icon
                    const isActive = activeFilters.some((f) => f.key === qf.key)
                    return (
                      <button
                        key={qf.key}
                        className={clsx('search-quick-filter', { active: isActive })}
                        onClick={() => isActive ? removeFilter(qf.key) : addFilter(qf.key, qf.label, qf.value)}
                      >
                        <Icon size={12} />
                        {qf.label}
                      </button>
                    )
                  })}
                </div>

                <div className="search-dropdown__divider" />
                <div className="search-dropdown__section-label">Search operators</div>
                <div className="search-dropdown__operators">
                  {[
                    ['from:', 'Filter by sender'],
                    ['to:', 'Filter by recipient'],
                    ['subject:', 'Filter by subject'],
                    ['after:', 'After date (YYYY-MM-DD)'],
                    ['before:', 'Before date'],
                    ['label:', 'Filter by label'],
                  ].map(([op, desc]) => (
                    <button
                      key={op}
                      className="search-operator"
                      onMouseDown={(e) => {
                        e.preventDefault()
                        setSearchQuery(op)
                        inputRef.current?.focus()
                      }}
                    >
                      <code className="search-operator__key">{op}</code>
                      <span className="search-operator__desc">{desc}</span>
                    </button>
                  ))}
                </div>
              </>
            )}

            {searchQuery !== '' && (
              <div className="search-dropdown__live">
                <div className="search-dropdown__section-label">
                  Press Enter to search all mail
                </div>
              </div>
            )}
          </motion.div>
        )}
      </AnimatePresence>

      <style>{`
        .search-bar {
          position: relative;
          flex: 1;
          max-width: 580px;
        }

        .search-bar__inner {
          display: flex;
          align-items: center;
          gap: 6px;
          padding: 0 10px 0 12px;
          height: 36px;
          background: var(--bg-elevated);
          border: 1.5px solid transparent;
          border-radius: var(--radius-full);
          transition: border-color var(--duration-fast), background var(--duration-fast);
          overflow: hidden;
        }

        .search-bar--focused .search-bar__inner,
        .search-bar__inner:focus-within {
          border-color: var(--border-focus);
          background: var(--bg-surface);
        }

        .search-bar__icon {
          color: var(--text-tertiary);
          flex-shrink: 0;
          transition: color var(--duration-fast);
        }

        .search-bar--focused .search-bar__icon {
          color: var(--amber-dim);
        }

        .search-bar__input {
          flex: 1;
          border: none;
          background: transparent;
          color: var(--text-primary);
          font-family: var(--font-ui);
          font-size: 13px;
          outline: none;
          min-width: 80px;
        }
        .search-bar__input::placeholder {
          color: var(--text-tertiary);
        }

        .search-bar__actions {
          display: flex;
          align-items: center;
          gap: 2px;
          flex-shrink: 0;
        }

        .search-bar__filter-btn.active {
          color: var(--brand);
          background: var(--brand-subtle);
        }

        .search-filter-chip {
          display: inline-flex;
          align-items: center;
          gap: 4px;
          padding: 2px 6px;
          background: var(--brand-subtle);
          color: var(--brand);
          border-radius: 3px;
          font-size: 11px;
          font-weight: 500;
          flex-shrink: 0;
          white-space: nowrap;
        }

        .search-filter-chip__remove {
          background: none;
          border: none;
          cursor: pointer;
          color: var(--brand);
          display: flex;
          align-items: center;
          padding: 0;
        }

        .search-dropdown {
          position: absolute;
          top: calc(100% + 6px);
          left: 0;
          right: 0;
          background: var(--bg-surface);
          border: 1px solid var(--border-strong);
          border-radius: var(--radius-lg);
          box-shadow: var(--shadow-lg);
          z-index: 500;
          padding: 8px 0;
          overflow: hidden;
        }

        .search-dropdown__section-label {
          padding: 6px 14px 4px;
          font-size: 10px;
          font-weight: 600;
          letter-spacing: 0.06em;
          text-transform: uppercase;
          color: var(--text-tertiary);
        }

        .search-dropdown__quick-filters {
          display: flex;
          flex-wrap: wrap;
          gap: 6px;
          padding: 4px 14px 10px;
        }

        .search-quick-filter {
          display: inline-flex;
          align-items: center;
          gap: 5px;
          padding: 4px 10px;
          background: var(--bg-elevated);
          border: 1px solid var(--border);
          border-radius: var(--radius-full);
          font-size: 12px;
          color: var(--text-secondary);
          cursor: pointer;
          transition: all var(--duration-fast);
        }
        .search-quick-filter:hover {
          border-color: var(--border-focus);
          color: var(--text-primary);
        }
        .search-quick-filter.active {
          background: var(--brand-subtle);
          border-color: var(--brand);
          color: var(--brand);
        }

        .search-dropdown__divider {
          height: 1px;
          background: var(--border);
          margin: 6px 0;
        }

        .search-dropdown__operators {
          padding: 4px 0 4px;
        }

        .search-operator {
          display: flex;
          align-items: center;
          gap: 12px;
          width: 100%;
          padding: 7px 14px;
          border: none;
          background: transparent;
          cursor: pointer;
          text-align: left;
          transition: background var(--duration-fast);
        }
        .search-operator:hover { background: var(--bg-hover); }

        .search-operator__key {
          font-family: var(--font-mono);
          font-size: 12px;
          color: var(--amber-dim);
          width: 80px;
          flex-shrink: 0;
        }

        .search-operator__desc {
          font-size: 12px;
          color: var(--text-tertiary);
        }
      `}</style>
    </div>
  )
}
